import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useEvidenciaPrace } from './context/EvidenciaPraceContext';
import './Kalkulacka.css';

const ACCOUNTING_TYPE_SERVICES = {
  'u1': 'Jednoduché účtovníctvo – neplatca DPH',
  'u2': 'Jednoduché účtovníctvo – platca DPH',
  'u3': 'Podvojné účtovníctvo – neplatca DPH',
  'u4': 'Podvojné účtovníctvo – platca DPH'
};

const SERVICE_CATEGORIES = [
  { id: 'pausal', label: 'Paušálne balíky', match: ['Jednoduché účtovníctvo', 'Podvojné účtovníctvo'] },
  { id: 'polozkove', label: 'Položkové účtovníctvo', match: ['VFA', 'DFA', 'Pohľadávky', 'Záväzky', 'Banka', 'Pokladňa', 'ID', 'Podanie DPH', 'Vystavovanie faktúr'] },
  { id: 'mzdy', label: 'Mzdy a personalistika', match: ['Spracovanie mzdy', 'Registrácia', 'Odhlásenie zamestnanca', 'Pracovná zmluva', 'Mesačné výkazy', 'Ročné zúčtovanie', 'Ročné hlásenie'] },
  { id: 'controlling', label: 'Controlling služby', match: ['Predbežný HV', 'Analytický výstup', 'Cashflow', 'Porovnanie plán', 'Mesačný komentovaný report', 'Optimalizácia nákladov', 'Rozpočtovanie', 'Finančný plán'] },
  { id: 'dane', label: 'Daňové priznania', match: ['Účtovná závierka', 'Daňové priznanie', 'Daň z motorových vozidiel', 'Dobrovoľná registrácia na DPH', 'Predzávierkové operácie'] },
  { id: 'doplnkove', label: 'Doplnkové služby', match: ['Kniha jázd', 'Cestovný príkaz', 'Žiadosť o vrátenie DPH', 'Excel / reporty', 'Smernice', 'Admin hod sadzba', 'Komplexné spracovanie majetku', 'Ľahké zaradenie majetku'] },
  { id: 'zakladanie', label: 'Zakladanie a zmeny', match: ['Založenie', 'Zmena', 'Zrušenie'] },
  { id: 'priplatky', label: 'Príplatky', match: ['Príplatok'] }
];

const Kalkulacka = () => {
  const location = useLocation();
  const prefill = location.state?.prefill || null;
  const { services } = useEvidenciaPrace();

  const [selectedServices, setSelectedServices] = useState([]);
  const [clientInfo, setClientInfo] = useState({
    name: prefill?.company || '',
    ico: '',
    address: '',
    date: prefill?.date || new Date().toISOString().split('T')[0]
  });
  const [accountingType, setAccountingType] = useState(prefill?.accountingType || '');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [savePin, setSavePin] = useState('');
  const [saveNote, setSaveNote] = useState('');

  useEffect(() => {
    if (prefill?.accountingType) {
      const serviceName = ACCOUNTING_TYPE_SERVICES[prefill.accountingType];
      if (serviceName) {
        addService(serviceName);
      }
    }
  }, [prefill]);

  const catalogItems = Object.entries(services || {}).map(([name, price]) => ({ name, price }));

  const categorize = (serviceName) => {
    const lower = serviceName.toLowerCase();
    const category = SERVICE_CATEGORIES.find(cat => cat.match.some(keyword => lower.includes(keyword.toLowerCase())));
    return category ? category.label : 'Ostatné';
  };

  const catalogByCategory = catalogItems.reduce((acc, item) => {
    const category = categorize(item.name);
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  const formatPrice = (value) => `${Number(value || 0).toFixed(2)} `;

  const addService = (name) => {
    setSelectedServices(prev => {
      const existing = prev.find(item => item.name === name);
      if (existing) {
        return prev.map(item => item.name === name ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [
        ...prev,
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name,
          quantity: 1,
          price: services[name] || 0,
          customPrice: services[name] === 0,
          customPriceValue: services[name] === 0 ? '' : services[name],
          note: '',
          categoryName: categorize(name)
        }
      ];
    });
  };

  const updateService = (id, field, value) => {
    setSelectedServices(prev => prev.map(item => item.id === id ? ({
      ...item,
      [field]: field === 'quantity' ? Math.max(1, Number(value) || 1) : value
    }) : item));
  };

  const toggleCustomPrice = (id) => {
    setSelectedServices(prev => prev.map(item => item.id === id ? ({
      ...item,
      customPrice: !item.customPrice,
      customPriceValue: !item.customPrice ? item.price : ''
    }) : item));
  };

  const removeService = (id) => {
    setSelectedServices(prev => prev.filter(item => item.id !== id));
  };

  const totalSum = selectedServices.reduce((sum, service) => {
    const unit = service.customPrice ? Number(service.customPriceValue) || 0 : Number(service.price || 0);
    return sum + unit * Number(service.quantity || 0);
  }, 0);

  const handleExportCSV = () => {
    if (selectedServices.length === 0) {
      alert('Najprv pridajte aspoň jednu službu.');
      return;
    }

    const header = ['Názov služby', 'Kategória', 'Množstvo', 'Cena/jedn.', 'Spolu', 'Poznámka'];
    const rows = selectedServices.map(item => {
      const unit = item.customPrice ? Number(item.customPriceValue) || 0 : Number(item.price || 0);
      return [
        item.name,
        item.categoryName,
        item.quantity,
        unit.toFixed(2),
        (unit * item.quantity).toFixed(2),
        item.note || ''
      ];
    });

    const csvContent = [header, ...rows]
      .map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `kalkulacka_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSaveToEvidence = async () => {
    if (!savePin.trim()) {
      alert('Zadajte PIN pre uloženie do evidencie.');
      return;
    }
    if (selectedServices.length === 0) {
      alert('Najprv pridajte aspoň jednu službu.');
      return;
    }

    try {
      const entriesToSave = selectedServices.map(service => ({
        companyName: clientInfo.name,
        date: clientInfo.date,
        description: service.name,
        service: service.categoryName,
        quantity: service.quantity,
        unitPrice: service.customPrice ? Number(service.customPriceValue) || 0 : Number(service.price || 0),
        total: (service.customPrice ? Number(service.customPriceValue) || 0 : Number(service.price || 0)) * Number(service.quantity || 0),
        note: saveNote,
        invoiced: false,
        createdAt: new Date().toISOString()
      }));

      const response = await fetch('/php/work_tracking_api.php?action=add_entries&pin=' + encodeURIComponent(savePin), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries: entriesToSave })
      });
      const data = await response.json();
      if (data.success) {
        alert('Úspešne pridané do evidencie!');
        setShowSaveDialog(false);
        setSaveNote('');
      } else {
        alert('Chyba: ' + (data.message || 'Nepodarilo sa uložiť'));
      }
    } catch (error) {
      alert('Chyba pripojenia k serveru');
    }
  };

  const handleClientInfoChange = (field, value) => {
    setClientInfo(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="kalkulacka-container">
      <header className="kalkulacka-header">
        <h1>Kalkulačka služieb</h1>
        <p>Vyberte služby, nastavte množstvá a vlastné ceny, potom uložte alebo exportujte kalkuláciu.</p>
      </header>

      <div className="kalkulacka-grid">
        <section className="kalkulacka-panel">
          <h2>Informácie o klientovi</h2>
          <div className="kalkulacka-row">
            <label>Názov / Meno klienta</label>
            <input value={clientInfo.name} onChange={e => handleClientInfoChange('name', e.target.value)} placeholder="Názov klienta" />
          </div>
          <div className="kalkulacka-row">
            <label>IČO</label>
            <input value={clientInfo.ico} onChange={e => handleClientInfoChange('ico', e.target.value)} placeholder="IČO" />
          </div>
          <div className="kalkulacka-row">
            <label>Adresa</label>
            <input value={clientInfo.address} onChange={e => handleClientInfoChange('address', e.target.value)} placeholder="Adresa" />
          </div>
          <div className="kalkulacka-row">
            <label>Dátum</label>
            <input type="date" value={clientInfo.date} onChange={e => handleClientInfoChange('date', e.target.value)} />
          </div>

          <h2>Katalóg služieb</h2>
          {Object.keys(catalogByCategory).map(category => (
            <div key={category} className="kalkulacka-category">
              <h3>{category}</h3>
              <div className="kalkulacka-services">
                {catalogByCategory[category].map(item => (
                  <div key={item.name} className="kalkulacka-service-item">
                    <div>
                      <strong>{item.name}</strong>
                      <div className="kalkulacka-service-price">{formatPrice(item.price)}{item.price === 0 ? ' (dohodou)' : ''}</div>
                    </div>
                    <button type="button" onClick={() => addService(item.name)}>Pridať</button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        <section className="kalkulacka-panel">
          <h2>Vybrané služby</h2>
          {selectedServices.length === 0 ? (
            <p>Momentálne nie sú vybraté žiadne služby.</p>
          ) : (
            <div className="kalkulacka-selected">
              <table>
                <thead>
                  <tr>
                    <th>Položka</th>
                    <th>Množstvo</th>
                    <th>Cena/jedn.</th>
                    <th>Spolu</th>
                    <th>Akcia</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedServices.map(item => {
                    const price = item.customPrice ? Number(item.customPriceValue) || 0 : Number(item.price || 0);
                    return (
                      <tr key={item.id}>
                        <td>
                          <strong>{item.name}</strong>
                          <div className="kalkulacka-service-meta">{item.categoryName}</div>
                          <textarea value={item.note} onChange={e => updateService(item.id, 'note', e.target.value)} placeholder="Poznámka k položke" />
                        </td>
                        <td>
                          <input type="number" min="1" value={item.quantity} onChange={e => updateService(item.id, 'quantity', e.target.value)} />
                        </td>
                        <td>
                          <div className="kalkulacka-price-row">
                            <input type="number" step="0.01" value={item.customPrice ? item.customPriceValue : item.price} disabled={!item.customPrice} onChange={e => updateService(item.id, 'customPriceValue', e.target.value)} />
                            <button type="button" onClick={() => toggleCustomPrice(item.id)}>{item.customPrice ? 'Fixovať cenu' : 'Vlastná cena'}</button>
                          </div>
                        </td>
                        <td>{formatPrice(price * item.quantity)}</td>
                        <td><button type="button" onClick={() => removeService(item.id)}>✕</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="kalkulacka-summary">
            <div>Celkom:</div>
            <div className="kalkulacka-total">{formatPrice(totalSum)}</div>
          </div>

          <div className="kalkulacka-actions-row">
            <button type="button" onClick={() => window.print()}>Tlačiť</button>
            <button type="button" onClick={handleExportCSV}>Export CSV</button>
            <button type="button" onClick={() => setShowSaveDialog(true)}>Uložiť do evidencie</button>
          </div>
        </section>
      </div>

      {showSaveDialog && (
        <div className="kalkulacka-dialog">
          <div className="kalkulacka-dialog-inner">
            <h2>Uložiť do evidencie</h2>
            <p>Skontrolujte vybrané služby a zadajte PIN pre uloženie.</p>
            <div className="kalkulacka-row">
              <label>PIN</label>
              <input type="password" value={savePin} onChange={e => setSavePin(e.target.value)} placeholder="PIN" />
            </div>
            <div className="kalkulacka-row">
              <label>Poznámka</label>
              <textarea value={saveNote} onChange={e => setSaveNote(e.target.value)} placeholder="Poznámka pre celý záznam" />
            </div>
            <div className="kalkulacka-dialog-actions">
              <button type="button" onClick={() => setShowSaveDialog(false)}>Zrušiť</button>
              <button type="button" onClick={handleSaveToEvidence}>Potvrdiť uloženie</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Kalkulacka;
